'use client';
import React, { useEffect, useState } from 'react';
import { Button, Errors, Loader } from '@/components';
import {
  createScheduledItemGroups,
  getNextItemEligibleForPrepayExtension,
  getPrepayBundleOffers,
  TypeScheduledItemGroup,
} from '@/components/account/PlayKitSchedule/utils';
import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';
import { TypeChildRecord } from '@/lib/api/accountManager/types';
import { TypeDecoratedSubscriptionRecord } from '@/lib/api/subscriptionManager/decorators/decorateSubscriptions';
import { BUTTON_SIZES, BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import useBundleDiscount from '@/lib/api/productCatalog/hooks/useBundleDiscount';
import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { ERROR_KINDS } from '@/core/constants';
import useDraftOrders from '@/lib/api/orderManager/hooks/useDraftOrders';
import useCustomerOrders from '@/lib/api/orderManager/hooks/useCustomerOrders';
import { TypeDecoratedUserRecord } from '@/lib/api/accountManager/decorators/decorateUser';
import usePlayKitBundleOffers from '@/lib/api/productCatalog/hooks/usePlayKitBundleOffers';
import useLocale from '@/hooks/useLocale';
import {
  getCurrencyFromLocale,
  getLanguageFromLocale,
} from '@/utils/translations/localizationHelpers';
import { TypeBanner } from '@/core/generated-types';
import {
  CurrentPlayKit,
  NextPlayKit,
  UpcomingPlayKit,
} from '@/components/account';

export type LayoutActiveSubscriptionProps = {
  t: TypePlayKitPageTranslations;
  selectedChild: TypeChildRecord;
  selectedSubscription: TypeDecoratedSubscriptionRecord;
  user: TypeDecoratedUserRecord;
  noticeBanner?: TypeBanner;
  holidayBanner?: TypeBanner;
  prepayBanner?: TypeBanner;
};

function LayoutActiveSubscription({
  t,
  selectedChild,
  selectedSubscription,
  user,
  noticeBanner,
  prepayBanner,
  holidayBanner,
}: LayoutActiveSubscriptionProps) {
  const [showHiddenKits, setShowHiddenKits] = useState<boolean>(false);
  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.DEFAULT,
  );

  useEffect(() => {
    // when new child is selected, close upcoming kit drawers
    setShowHiddenKits(false);
  }, [selectedChild]);

  const locale = useLocale();
  const language = getLanguageFromLocale(locale);
  const currency = getCurrencyFromLocale(locale);
  const { customerOrdersData, customerOrdersLoading, customerOrdersError } =
    useCustomerOrders(selectedSubscription.recipient_uuid);

  const { draftOrdersData, draftOrdersLoading, draftOrdersError } =
    useDraftOrders(
      selectedSubscription.recipient_uuid,
      selectedSubscription.uuid,
    );

  const { bundleDiscountData, bundleDiscountLoading, bundleDiscountError } =
    useBundleDiscount(selectedSubscription.bundle_discount_uuid);

  const nextItemEligibleForPrepayExtension =
    getNextItemEligibleForPrepayExtension(selectedSubscription);
  const isPrepayExtensionDisabled = !nextItemEligibleForPrepayExtension;
  const {
    playKitBundleOffersData,
    playKitBundleOffersLoading,
    playKitBundleOffersError,
  } = usePlayKitBundleOffers({
    language: language,
    currencyCode: currency,
    productAnchor: nextItemEligibleForPrepayExtension?.product?.anchor,
    shouldFetch: !isPrepayExtensionDisabled,
  });

  if (
    bundleDiscountLoading ||
    draftOrdersLoading ||
    customerOrdersLoading ||
    playKitBundleOffersLoading
  ) {
    return (
      <div data-testid="layout-active-subscription">
        <Loader loading text={t.loading} />
      </div>
    );
  }

  if (
    bundleDiscountError ||
    draftOrdersError ||
    customerOrdersError ||
    playKitBundleOffersError
  ) {
    return (
      <div data-testid="layout-active-subscription">
        <Errors errors={errors} heading={heading} />
      </div>
    );
  }

  const draftOrder = draftOrdersData?.draft_orders?.[0];
  const { currentGroup, nextGroups, upcomingGroups } =
    createScheduledItemGroups({
      subscription: selectedSubscription,
      t,
      bundleDiscountData,
      draftOrderData: draftOrder,
      ordersData: customerOrdersData?.orders,
      userData: user,
    });

  const UPCOMING_KIT_PREVIEW_CUTOFF = 3;
  const hasHiddenUpcomingKits =
    upcomingGroups.length > UPCOMING_KIT_PREVIEW_CUTOFF;
  const visibleUpcomingPlayKits = showHiddenKits
    ? upcomingGroups
    : upcomingGroups.slice(0, UPCOMING_KIT_PREVIEW_CUTOFF);
  const prepayBundleExtensionOffers = getPrepayBundleOffers(
    playKitBundleOffersData,
  );

  return (
    <div data-testid="layout-active-subscription">
      {currentGroup && (
        <CurrentPlayKit
          t={t}
          group={currentGroup}
          noticeBanner={noticeBanner}
        />
      )}
      {nextGroups &&
        nextGroups.map((group, index) => (
          <NextPlayKit
            key={group.items?.[0].uuid}
            t={t}
            group={group}
            isFirstGroup={index === 0}
            hasManyGroups={
              nextGroups.filter((group) => group.isUpcomingOrderPreview)
                ?.length > 1
            }
            prepayBundleExtensionOffers={prepayBundleExtensionOffers}
            prepayBanner={prepayBanner}
            holidayBanner={holidayBanner}
          />
        ))}
      {visibleUpcomingPlayKits &&
        visibleUpcomingPlayKits.map(
          (upcomingKitGroup: TypeScheduledItemGroup, index: number) => (
            <UpcomingPlayKit
              key={upcomingKitGroup.items?.[0].uuid}
              t={t}
              group={upcomingKitGroup}
              isFirstGroup={index === 0}
              prepayBanner={prepayBanner}
              holidayBanner={holidayBanner}
              prepayBundleExtensionOffers={prepayBundleExtensionOffers}
            />
          ),
        )}
      {hasHiddenUpcomingKits && (
        <Button
          onClick={() => setShowHiddenKits(!showHiddenKits)}
          ctaText={showHiddenKits ? t.showLessKits : t.showMoreKits}
          size={BUTTON_SIZES.lg}
          style={BUTTON_STYLES.secondary}
        />
      )}
    </div>
  );
}

export default LayoutActiveSubscription;
